import { isSafariBrowser } from '@/lib/browser'
import { cn } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
// Resolve the worker from the bare pdfjs-dist specifier. The direct
// pdfjs-dist dependency is pinned to react-pdf's exact version so the two
// dedupe to a single install, keeping the worker and API versions in sync.
// Importing via react-pdf/node_modules/... is rejected by the package
// "exports" map (targets may not contain /node_modules/ segments).
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc

const PDF_WASM_URL = import.meta.env.DEV
  ? '/node_modules/pdfjs-dist/wasm/'
  : `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`

interface AgentPdfDocumentViewerProps {
  url: string
  title: string
  className?: string
}

export function AgentPdfDocumentViewer({ url, title, className }: AgentPdfDocumentViewerProps) {
  const measureRef = useRef<HTMLDivElement>(null)
  const [pageWidth, setPageWidth] = useState(600)
  const [numPages, setNumPages] = useState(0)
  const [pdfData, setPdfData] = useState<{ data: ArrayBuffer } | null>(null)
  const [isFetching, setIsFetching] = useState(true)
  const [hasError, setHasError] = useState(false)

  const pdfOptions = useMemo(
    () => ({
      wasmUrl: PDF_WASM_URL,
      ...(isSafariBrowser()
        ? {
            isImageDecoderSupported: false,
            isOffscreenCanvasSupported: false,
          }
        : {}),
    }),
    [],
  )

  useEffect(() => {
    let cancelled = false
    setIsFetching(true)
    setHasError(false)
    setPdfData(null)
    setNumPages(0)

    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`PDF fetch failed: ${response.status}`)
        }
        return response.arrayBuffer()
      })
      .then((buffer) => {
        if (!cancelled) {
          setPdfData({ data: buffer })
          setIsFetching(false)
        }
      })
      .catch((error) => {
        console.error('Agent PDF fetch error:', error)
        if (!cancelled) {
          setHasError(true)
          setIsFetching(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [url])

  useEffect(() => {
    const element = measureRef.current
    if (!element) return

    const measure = () => {
      setPageWidth(Math.max(240, element.clientWidth))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    if (numPages > 0 && measureRef.current) {
      setPageWidth(Math.max(240, measureRef.current.clientWidth))
    }
  }, [numPages])

  if (isFetching) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-3 py-16', className)}>
        <Loader2 className="h-8 w-8 animate-spin text-status-500" aria-hidden />
        <p className="text-sm text-muted-foreground">Loading PDF...</p>
      </div>
    )
  }

  if (hasError || !pdfData) {
    return (
      <div className={cn('px-4 py-12 text-center text-sm text-muted-foreground', className)}>
        Could not load this PDF preview.
      </div>
    )
  }

  return (
    <div ref={measureRef} data-document-surface className={cn('w-full', className)}>
      <Document
        file={pdfData}
        options={pdfOptions}
        loading={null}
        onLoadSuccess={({ numPages: totalPages }) => {
          setHasError(false)
          setNumPages(totalPages)
        }}
        onLoadError={(error) => {
          console.error('Agent PDF render error:', error)
          setHasError(true)
          setNumPages(0)
        }}
      >
        <div className="flex flex-col items-center gap-4">
          {numPages > 0
            ? Array.from({ length: numPages }, (_, index) => index + 1).map((pageNumber) => (
                <Page
                  key={pageNumber}
                  pageNumber={pageNumber}
                  width={pageWidth}
                  className="overflow-hidden rounded-sm bg-card shadow-[0_2px_12px_rgba(15,23,42,0.08)]"
                  canvasBackground="white"
                  loading={null}
                  renderTextLayer
                  renderAnnotationLayer
                  aria-label={`${title} page ${pageNumber} of ${numPages}`}
                />
              ))
            : null}
        </div>
      </Document>
    </div>
  )
}
